import { useState } from 'react';

import {
  toProfileUpdate,
  validateProfileForm,
  type ProfileFieldErrors,
  type ProfileFormValues,
} from '../features/employees/application/profileForm';
import type { MyProfileUpdate } from '../features/employees/application/ports/employeePort';
import { Alert, Button, Card, DateField, SelectField, TextField } from '../shared/components';

import styles from './ScreenLayout.module.css';

/**
 * Profile completion — the fields the roster import could not supply.
 *
 * Validation lives in `profileForm.ts` and is tested without rendering. This
 * screen shows the result (§5.4).
 */
export interface ProfilePageProps {
  readonly employeeNumber: string;
  readonly fullName: string;
  readonly branchNameAr: string | null;
  readonly initialValues: ProfileFormValues;
  readonly outstanding: readonly string[];
  readonly today: string;
  readonly onSave: (update: MyProfileUpdate) => Promise<void>;
  readonly onSignOut: () => void;
  readonly errorAr: string | null;
  readonly savedAr: string | null;
  /**
   * Where the employee goes next, once nothing on this screen is outstanding.
   *
   * Saving used to leave them on the same form with a green message and no
   * indication that three more sections existed. Null when this was the last
   * one.
   */
  readonly nextStep: { readonly labelAr: string; readonly onGo: () => void } | null;
}

export function ProfilePage({
  employeeNumber,
  fullName,
  branchNameAr,
  initialValues,
  outstanding,
  today,
  onSave,
  onSignOut,
  errorAr,
  savedAr,
  nextStep,
}: ProfilePageProps) {
  const [values, setValues] = useState<ProfileFormValues>(initialValues);
  const [errors, setErrors] = useState<ProfileFieldErrors>({});
  const [saving, setSaving] = useState(false);

  const set = (field: keyof ProfileFormValues) => (event: { target: { value: string } }) => {
    setValues((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const result = validateProfileForm(values, today);
    setErrors(result.errors);
    if (!result.valid) return;

    setSaving(true);
    void onSave(toProfileUpdate(values)).finally(() => {
      setSaving(false);
    });
  };

  return (
    <div className={styles.page}>
      <h1 className="visually-hidden">بياناتي</h1>

      <Card title="بياناتي" description={`الرقم الوظيفي: ${employeeNumber}`}>
        {errorAr ? <Alert severity="error">{errorAr}</Alert> : null}

        {savedAr ? (
          <Alert severity="success" title={savedAr}>
            {nextStep && outstanding.length === 0 ? (
              <div className={styles.nextStep}>
                <span>{`الخطوة التالية: ${nextStep.labelAr}`}</span>
                <Button type="button" size="sm" onClick={nextStep.onGo}>
                  {`المتابعة إلى ${nextStep.labelAr}`}
                </Button>
              </div>
            ) : (
              'حُفظت بياناتك.'
            )}
          </Alert>
        ) : null}

        {/* Naming what is missing, rather than "your profile is incomplete" —
            the vague version generates a phone call to HR. */}
        {outstanding.length > 0 ? (
          <Alert severity="warning" title="بيانات ناقصة">
            {`يلزم إكمال: ${outstanding.join(' · ')}`}
          </Alert>
        ) : null}

        {/* Read-only facts from the roster. Shown so the employee can check
            them, and reported to HR if wrong — they are not theirs to edit
            (spec FR-011). */}
        <div className={styles.formGrid}>
          <TextField label="الاسم" value={fullName} readOnly disabled />
          <TextField label="الفرع" value={branchNameAr ?? 'غير محدد'} readOnly disabled />
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className={styles.formGrid}>
            <TextField
              label="رقم الهوية"
              required
              numeric
              inputMode="numeric"
              value={values.nationalId}
              onChange={set('nationalId')}
              error={errors.nationalId}
            />
            <DateField
              label="تاريخ الميلاد"
              required
              value={values.birthDate}
              onChange={(birthDate) => {
                setValues((current) => ({ ...current, birthDate }));
              }}
              error={errors.birthDate}
            />
            <SelectField
              label="الجنس"
              required
              value={values.gender}
              onChange={set('gender')}
              error={errors.gender}
            >
              <option value="" disabled>
                اختر…
              </option>
              <option value="male">ذكر</option>
              <option value="female">أنثى</option>
            </SelectField>
            <TextField
              label="رقم الجوال"
              required
              numeric
              inputMode="numeric"
              autoComplete="tel"
              placeholder="7XXXXXXXX"
              value={values.phone}
              onChange={set('phone')}
              error={errors.phone}
              hint="9 أرقام تبدأ بـ 70 أو 71 أو 73 أو 77 أو 78"
            />
            <div className={styles.spanAll}>
              <TextField
                label="العنوان"
                required
                value={values.address}
                onChange={set('address')}
                error={errors.address}
              />
            </div>

            <div className={styles.spanAll}>
              <div className={styles.actions}>
                <Button type="submit" size="lg" fullWidth loading={saving}>
                  حفظ البيانات
                </Button>
                <Button variant="ghost" fullWidth onClick={onSignOut}>
                  تسجيل الخروج
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
}
